import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { DollarSign } from 'lucide-react';
import type { AnalyticsMetrics } from '@/types/ai-admin';

interface CostAnalysisChartProps {
  analytics: AnalyticsMetrics;
}

export default function CostAnalysisChart({ analytics }: CostAnalysisChartProps) {
  // Prepare data for pie chart (by model)
  const modelCostData = Object.entries(analytics.costBreakdown.byModel).map(([model, cost]) => ({
    name: model.includes('haiku') ? 'Claude Haiku' :
          model.includes('sonnet') ? 'Claude Sonnet' : 'Claude Opus',
    value: cost,
    percentage: ((cost / analytics.summary.totalCost) * 100).toFixed(1)
  }));

  // Prepare data for bar chart (by agent)
  const agentCostData = Object.entries(analytics.costBreakdown.byAgent).map(([agent, cost]) => ({
    name: agent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    cost,
    queries: Math.floor(cost / 0.003) // Approximate queries based on avg cost
  }));

  // Colors for charts
  const COLORS = {
    haiku: 'hsl(var(--chart-1))',
    sonnet: 'hsl(var(--chart-2))',
    opus: 'hsl(var(--chart-3))'
  };

  const AGENT_COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  // Custom label for pie chart
  const renderCustomLabel = (entry: any) => {
    return `${entry.percentage}%`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium">{label || payload[0].name}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm">
              {entry.name === 'cost' || entry.dataKey === 'value' ?
                `$${entry.value.toFixed(2)}` :
                `${entry.value} queries`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="col-span-1">
      <CardHeader>
        <CardTitle>Cost Analysis</CardTitle>
        <CardDescription>
          Breakdown of AI costs by model and agent
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="model" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="model">By Model</TabsTrigger>
            <TabsTrigger value="agent">By Agent</TabsTrigger>
          </TabsList>

          <TabsContent value="model" className="space-y-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={modelCostData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {modelCostData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.name.includes('Haiku') ? COLORS.haiku :
                          entry.name.includes('Sonnet') ? COLORS.sonnet :
                          COLORS.opus
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-2">
              {modelCostData.map((model, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{
                        backgroundColor:
                          model.name.includes('Haiku') ? COLORS.haiku :
                          model.name.includes('Sonnet') ? COLORS.sonnet :
                          COLORS.opus
                      }}
                    />
                    <span>{model.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">${model.value.toFixed(2)}</span>
                    <span className="text-muted-foreground">({model.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-1">💡 Cost Optimization Tip</p>
              <p className="text-xs text-muted-foreground">
                Move {Math.floor(modelCostData.find(m => m.name.includes('Sonnet'))?.value || 0 / 0.003 * 0.3)}
                {' '}simple queries from Sonnet to Haiku to save ~30% on costs.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="agent" className="space-y-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={agentCostData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="name"
                    className="text-xs"
                    tick={{ fill: 'currentColor' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: 'currentColor' }}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="cost" fill="hsl(var(--chart-1))" radius={[8, 8, 0, 0]}>
                    {agentCostData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={AGENT_COLORS[index % AGENT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm font-medium">
                <span>Most Expensive Agent</span>
                <span className="text-muted-foreground">
                  {agentCostData[0]?.name} (${agentCostData[0]?.cost.toFixed(2)})
                </span>
              </div>
              <div className="flex items-center justify-between text-sm font-medium">
                <span>Most Queries</span>
                <span className="text-muted-foreground">
                  {agentCostData.sort((a, b) => b.queries - a.queries)[0]?.name}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm font-medium">
                <span>Average Cost per Agent</span>
                <span className="text-muted-foreground">
                  ${(analytics.summary.totalCost / 6).toFixed(2)}
                </span>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}